import type {
  EtatMandatReprise,
  EtatSupport,
  LigneRepriseGalerie,
  MandatReprise,
  RecetteReprise,
} from './accesVenio.js';
import { DEMO_PDF_DOCUMENT_DATA_URI } from './demoFixtures.js';

/**
 * Accès Venio en mode démonstration : une demande de mandat et une demande de
 * support en attente, que l'on peut accorder, refuser puis retirer. Aucun nom
 * réel : des adresses de démonstration.
 */
const JOUR = 86_400_000;

function dans(ms: number): string {
  return new Date(Date.now() + ms).toISOString();
}

function etatInitial(): { mandat: EtatMandatReprise; support: EtatSupport } {
  const demande: MandatReprise = {
    id: 'demo-mandat-demande',
    etat: 'en_attente',
    source: 'galerie',
    natures: ['oeuvres', 'contacts', 'documents'],
    confidentiels: false,
    reference: '',
    motif: 'Reprise du fonds et du carnet d’adresses depuis l’ancien logiciel',
    dureeJours: 14,
    demandePar: 'exploitation@venio.test',
    accordePar: null,
    demandeLe: dans(-JOUR),
    accordeLe: null,
    expireLe: dans(6 * JOUR),
    revoqueLe: null,
    revoqueDepuis: null,
  };
  return {
    mandat: { actif: null, demandes: [demande], historique: [demande] },
    support: {
      demandes: [
        {
          id: 'demo-support-demande',
          demandeurEmail: 'support@venio.test',
          motif: 'Comprendre pourquoi une facture ne peut pas être émise',
          role: 'lecture',
          etat: 'en_attente',
          demandeLe: dans(-3_600_000),
          expireLe: dans(7 * JOUR),
          trancheLe: null,
        },
      ],
      acces: [],
    },
  };
}

let etat = etatInitial();

/**
 * La recette de la reprise, à valider : des compteurs par nature, dont deux ne
 * tombent pas juste (l'écart se lit à l'écran), deux vérifications faites. Valider
 * la fait passer à « validée » avec son procès-verbal ; signaler ajoute un message.
 *
 * Mêmes natures, mêmes chiffres, mêmes vérifications que le dossier Aubade du
 * mode démo de la console (`console/src/lib/demoOuvertures.ts`, en recette) :
 * la galerie et Venio regardent la même reprise. Un changement ici se refait là.
 */
function ligneRecette(
  nature: LigneRepriseGalerie['nature'],
  total: number,
  crees: number,
  completes: number,
  ignores: number,
  refuses: number,
  ecartes: number,
  aRevoir = 0,
): LigneRepriseGalerie {
  return {
    nature,
    total,
    crees,
    completes,
    ignores,
    refuses,
    ecartes,
    ecart: total - (crees + completes + ignores + refuses + ecartes),
    aRevoir,
  };
}

function recetteInitiale(): RecetteReprise {
  return {
    numero: 'OUV-2026-005',
    etat: 'recette',
    natures: [
      ligneRecette('lieux', 14, 12, 2, 0, 0, 0),
      ligneRecette('artistes', 86, 79, 4, 3, 0, 0),
      ligneRecette('oeuvres', 1_252, 1_201, 0, 18, 6, 23, 3),
      ligneRecette('images', 1_410, 1_187, 0, 211, 12, 0),
      ligneRecette('localisations', 2_904, 2_890, 0, 0, 14, 0),
      ligneRecette('contacts', 3_519, 3_377, 41, 64, 30, 0, 5),
      ligneRecette('factures-historiques', 642, 642, 0, 0, 0, 0),
    ],
    aRevoir: 8,
    controles: [
      { cle: 'historique_localisations', coche: true, le: dans(-2 * JOUR) },
      { cle: 'echantillon_fiches', coche: false, le: null },
      { cle: 'images_principales', coche: true, le: dans(-2 * JOUR) },
      { cle: 'factures_anterieures', coche: false, le: null },
    ],
    validation: null,
    signalements: [],
    peutValider: true,
    basculePrevueLe: dans(18 * JOUR),
  };
}

let recette = recetteInitiale();

/** Réponse de démonstration des routes de recette ; `undefined` : ce n'est pas la leur. */
function reponseRecetteDemo(chemin: string, methode: string, corps?: unknown): unknown {
  if (chemin === '/mandat-reprise/recette' && methode === 'GET') return { recette };
  if (chemin === '/mandat-reprise/recette/valider' && methode === 'POST') {
    if (recette.validation === null) {
      recette = {
        ...recette,
        peutValider: false,
        validation: {
          par: 'direction@sainte-avoye.example',
          le: dans(0),
          pv: { disponible: true, rangeAuCoffre: true },
        },
      };
    }
    return { recette };
  }
  if (chemin === '/mandat-reprise/recette/signaler' && methode === 'POST') {
    const { texte } = (corps ?? {}) as { texte?: string };
    recette = {
      ...recette,
      signalements: [
        ...recette.signalements,
        { texte: texte ?? '', par: 'direction@sainte-avoye.example', le: dans(0) },
      ],
    };
    return { recette };
  }
  if (chemin === '/mandat-reprise/recette/pv' && methode === 'GET') {
    return {
      url: DEMO_PDF_DOCUMENT_DATA_URI,
      expireLe: dans(120_000),
      expireDansSecondes: 120,
      sha256: 'a3f1c2d4e5b60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    };
  }
  return undefined;
}

/** Réponse de démonstration d'une route d'accès Venio ; `undefined` : ce n'est pas la leur. */
export function reponseAccesVenioDemo(
  chemin: string,
  methode: string,
  corps?: unknown,
): unknown | undefined {
  const gesteRecette = reponseRecetteDemo(chemin, methode, corps);
  if (gesteRecette !== undefined) return gesteRecette;
  if (chemin === '/mandat-reprise' && methode === 'GET') return etat.mandat;
  if (chemin === '/support-access' && methode === 'GET') return etat.support;

  if (chemin === '/mandat-reprise/accorder' && methode === 'POST') {
    const { demandeId } = (corps ?? {}) as { demandeId?: string };
    const demande = etat.mandat.demandes.find((ligne) => ligne.id === demandeId);
    if (!demande) return undefined;
    const accorde: MandatReprise = {
      ...demande,
      etat: 'actif',
      accordePar: 'direction@sainte-avoye.example',
      accordeLe: dans(0),
      expireLe: dans((demande.dureeJours ?? 1) * JOUR),
    };
    etat = {
      ...etat,
      mandat: {
        actif: accorde,
        demandes: [],
        historique: etat.mandat.historique.map((ligne) =>
          ligne.id === accorde.id ? accorde : ligne,
        ),
      },
    };
    return accorde;
  }
  if (chemin === '/mandat-reprise/refuser' && methode === 'POST') {
    const { demandeId } = (corps ?? {}) as { demandeId?: string };
    etat = {
      ...etat,
      mandat: {
        ...etat.mandat,
        demandes: etat.mandat.demandes.filter((ligne) => ligne.id !== demandeId),
        historique: etat.mandat.historique.map((ligne) =>
          ligne.id === demandeId ? { ...ligne, etat: 'refuse' } : ligne,
        ),
      },
    };
    return null;
  }
  if (chemin === '/mandat-reprise/revoquer' && methode === 'POST') {
    const { actif } = etat.mandat;
    etat = {
      ...etat,
      mandat: {
        actif: null,
        demandes: [],
        historique: etat.mandat.historique.map((ligne) =>
          ligne.id === actif?.id ? { ...ligne, etat: 'revoque' } : ligne,
        ),
      },
    };
    return null;
  }

  const gesteSupport = /^\/support-access\/([^/]+)\/(accorder|refuser)$/.exec(chemin);
  if (gesteSupport?.[1] && methode === 'POST') {
    const demande = etat.support.demandes.find((ligne) => ligne.id === gesteSupport[1]);
    if (!demande) return undefined;
    if (gesteSupport[2] === 'refuser') {
      etat = {
        ...etat,
        support: {
          ...etat.support,
          demandes: etat.support.demandes.map((ligne) =>
            ligne.id === demande.id ? { ...ligne, etat: 'refusee', trancheLe: dans(0) } : ligne,
          ),
        },
      };
      return null;
    }
    const { dureeHeures } = (corps ?? {}) as { dureeHeures?: number };
    const expireLe = dans((dureeHeures ?? 24) * 3_600_000);
    etat = {
      ...etat,
      support: {
        demandes: etat.support.demandes.map((ligne) =>
          ligne.id === demande.id ? { ...ligne, etat: 'accordee', trancheLe: dans(0) } : ligne,
        ),
        acces: [
          {
            id: 'demo-acces-support',
            supportEmail: demande.demandeurEmail,
            motif: demande.motif,
            role: demande.role,
            accordeLe: dans(0),
            expireLe,
            revoqueLe: null,
            actif: true,
          },
        ],
      },
    };
    return { id: 'demo-acces-support', actif: true, expireLe };
  }
  if (chemin === '/support-access' && methode === 'DELETE') {
    etat = {
      ...etat,
      support: {
        demandes: etat.support.demandes.map((ligne) =>
          ligne.etat === 'accordee' ? { ...ligne, etat: 'revoquee' } : ligne,
        ),
        acces: etat.support.acces.map((acces) => ({ ...acces, actif: false, revoqueLe: dans(0) })),
      },
    };
    return null;
  }
  return undefined;
}
